import { useEffect, useState, type FormEvent } from "react";
import { Boxes, KeyRound } from "lucide-react";
import { api, post } from "./api";
import { Button, Card, InlineNotice } from "./components/ui";
import { useI18n } from "./i18n";

export function AccountLogin({
  notice,
  onSuccess,
}: {
  notice?: string;
  onSuccess: (recovered?: boolean) => void;
}) {
  const { t } = useI18n();
  const [stage, setStage] = useState<
      "password" | "totp" | "recovery" | "reset"
    >("password"),
    [username, setUsername] = useState("");
  const [options, setOptions] = useState({
      oidc: false,
      password_reset: false,
    }),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    void api<typeof options>("/account/login-options")
      .then(setOptions)
      .catch(() => undefined);
  }, []);
  function go(next: typeof stage) {
    setStage(next);
    setError("");
    setMessage("");
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setMessage("");
    setBusy(true);
    const form = event.currentTarget,
      data = new FormData(form);
    try {
      if (stage === "password") {
        const name = String(data.get("username"));
        const response = await post<{ status?: string }>("/login", {
          username: name,
          password: data.get("password"),
        });
        setUsername(name);
        form.reset();
        if (response.status === "mfa_required") {
          setStage("totp");
          return;
        }
        onSuccess();
      } else if (stage === "totp" || stage === "recovery") {
        const result = await post<{ rebind_recommended?: boolean }>(
          stage === "totp" ? "/login/totp" : "/login/recovery",
          { code: data.get("code") },
        );
        form.reset();
        if (result.rebind_recommended)
          sessionStorage.setItem("layla-rebind-recommended", "true");
        onSuccess(result.rebind_recommended);
      } else {
        if (data.get("new_password") !== data.get("confirm"))
          throw new Error("新密码与确认值不一致");
        await post("/password/reset", {
          username: data.get("username"),
          recovery_code: data.get("recovery_code"),
          code: data.get("code") || "",
          new_password: data.get("new_password"),
        });
        form.reset();
        setStage("password");
        setMessage("密码已重置，旧密码失效，全部设备已退出。请重新登录。");
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="login-page">
      <Card className="login-card">
        <div className="login-heading">
          <span className="login-logo">
            <Boxes />
          </span>
          <h1>
            {stage === "totp"
              ? "验证动态码"
              : stage === "recovery"
                ? "恢复验证器"
                : stage === "reset"
                  ? "找回密码"
                  : t("Sign in to Layla TAB")}
          </h1>
          <p>
            {stage === "totp"
              ? `${username}：密码已验证，请完成第二步。`
              : stage === "recovery"
                ? "使用预先保存的 TOTP 恢复码。成功后旧验证器和其余会话失效，请重新绑定。"
                : stage === "reset"
                  ? "使用独立的密码恢复凭据；不能用 TOTP 恢复码替代。"
                  : t(
                      "Manage contracts, execution nodes, tasks, and artifacts from one control plane.",
                    )}
          </p>
        </div>
        {notice && <InlineNotice tone="warning">{t(notice)}</InlineNotice>}
        {message && <InlineNotice tone="success">{message}</InlineNotice>}
        <form key={stage} className="login-form" onSubmit={submit}>
          {(stage === "password" || stage === "reset") && (
            <label className="field">
              <span>{t("Username")}</span>
              <input
                name="username"
                defaultValue={username}
                autoComplete="username"
                required
              />
            </label>
          )}
          {stage === "password" && (
            <label className="field">
              <span>{t("Password")}</span>
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                required
              />
            </label>
          )}
          {(stage === "totp" || stage === "recovery") && (
            <label className="field">
              <span>
                {stage === "totp" ? "动态验证码" : "一次性 TOTP 恢复码"}
              </span>
              <input
                name="code"
                inputMode={stage === "totp" ? "numeric" : "text"}
                pattern={stage === "totp" ? "[0-9]{6}" : undefined}
                maxLength={stage === "totp" ? 6 : 100}
                autoComplete="one-time-code"
                required
              />
            </label>
          )}
          {stage === "reset" && (
            <>
              <label className="field">
                <span>独立密码恢复凭据</span>
                <input name="recovery_code" autoComplete="off" required />
              </label>
              <label className="field">
                <span>动态验证码（已开启登录 TOTP 时必填）</span>
                <input
                  name="code"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </label>
              <label className="field">
                <span>新密码（8–128 个 Unicode 字符）</span>
                <input
                  name="new_password"
                  type="password"
                  autoComplete="new-password"
                  required
                />
              </label>
              <label className="field">
                <span>确认新密码</span>
                <input
                  name="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                />
              </label>
            </>
          )}
          {error && <InlineNotice tone="error">{error}</InlineNotice>}
          <Button
            className="full-width"
            variant="primary"
            type="submit"
            disabled={busy}
          >
            {busy
              ? t("Signing in…")
              : stage === "password"
                ? t("Sign in")
                : stage === "reset"
                  ? "验证并重置"
                  : "验证并继续"}
          </Button>
        </form>
        <div className="account-login-actions">
          {stage === "password" ? (
            <>
              {options.oidc && (
                <a
                  className="ui-button button-outline full-width"
                  href="/api/ui/v1/auth/oidc/start"
                >
                  <KeyRound />
                  {t("Continue with OIDC")}
                </a>
              )}
              {options.password_reset && (
                <Button variant="ghost" onClick={() => go("reset")}>
                  使用恢复凭据找回密码
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => go("password")}
              >
                返回密码登录
              </Button>
              {stage === "totp" && (
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() => go("recovery")}
                >
                  无法使用验证器？使用恢复码
                </Button>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
